import { useState, useEffect, useRef } from 'react'
import hasElementOrSomeParentTheClass from '@Utils/HasElementOrSomeParentTheClass'

interface UseEditableNameProps {
  name: string
  saving: boolean
  hasRenamedSuccessfully: boolean
  handleRename: (name: string) => void
  inputClassName: string
}

export default function useEditableName({
  name,
  saving,
  hasRenamedSuccessfully,
  handleRename,
  inputClassName
}: UseEditableNameProps) {
  const [editing, setEditing] = useState(false)
  const [editedName, setEditedName] = useState(name)
  const [inputWidth, setInputWidth] = useState<number | undefined>()

  const editingRef = useRef(editing)
  const editedNameRef = useRef(editedName)
  const nameContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setEditedName(name)
  }, [name])

  useEffect(() => {
    editedNameRef.current = editedName
  }, [editedName])

  useEffect(() => {
    editingRef.current = editing
    if (editing && inputRef.current) {
      inputRef.current.select()
    }
  }, [editing])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!editingRef.current) { return }

      const element = e.target as HTMLElement
      if (hasElementOrSomeParentTheClass(element, inputClassName)) { return }

      save()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!editingRef.current) { return }

      if (event.code === 'Escape') {
        cancel()
        return
      }
      if (event.code === 'Enter') {
        if (!editedNameRef.current.trim()) {
          cancel()
        } else {
          save()
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    if (!saving && hasRenamedSuccessfully) {
      cancel()
    }
  }, [saving, hasRenamedSuccessfully])

  const startEditing = () => {
    if (nameContainerRef.current) {
      setInputWidth(nameContainerRef.current.offsetWidth)
    }
    setEditedName(name)
    setEditing(true)
  }

  const changeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedName(e.target.value)
  }

  const cancel = () => {
    setEditedName(name)
    setEditing(false)
  }

  const save = () => {
    handleRename(editedNameRef.current.trim())
  }

  return {
    editing,
    editedName,
    inputWidth,
    nameContainerRef,
    inputRef,
    startEditing,
    changeName,
    cancel,
    save,
  }
}
